import { computed, reactive, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import type {
  MonitorGroup,
  MonitorServiceView,
  MonitorTaskKind,
  MonitorTaskPage,
  MonitorTaskQuery,
  MonitorTaskView,
  SystemMonitorOverview,
  MonitorOnlineUserPage,
  MonitorOnlineUserQuery,
} from 'shared';
import { adminApi } from '@/api';

export const useAdminRuntimeMonitorStore = defineStore('admin-runtime-monitor', () => {
  const overview = ref<SystemMonitorOverview>();
  const overviewLoading = ref(false);
  const overviewError = ref('');
  const autoRefresh = ref(true);
  const onlineUsers = ref<MonitorOnlineUserPage>();
  const onlineLoading = ref(false);
  const onlineError = ref('');
  const onlineQuery = reactive<Required<MonitorOnlineUserQuery>>({
    search: '',
    client: 'all',
    state: 'all',
    page: 1,
  });
  const onlineKey = computed(
    () =>
      `/admin/monitor/online-users?${new URLSearchParams(Object.entries(onlineQuery).map(([key, value]) => [key, String(value)]))}`,
  );
  const loadedOnlineKey = ref('');
  const onlineCurrent = computed(() => loadedOnlineKey.value === onlineKey.value);
  let onlineRequest = 0;
  let requestedOnlineKey = '';
  type ServiceFilter = {
    group: MonitorGroup | 'all';
    status: 'all' | 'abnormal' | 'unknown';
    search: string;
  };
  const page = ref<'monitor' | 'services'>('monitor');
  const serviceFilters = reactive<Record<'monitor' | 'services', ServiceFilter>>({
    monitor: { group: 'all', status: 'abnormal', search: '' },
    services: { group: 'all', status: 'all', search: '' },
  });
  const serviceFilter = computed(() => serviceFilters[page.value]);
  const tab = ref<MonitorTaskKind>('agent');
  const queueId = ref('agent-runs');
  const queries = reactive(
    Object.fromEntries(
      (['agent', 'creative', 'queue'] as const).map((kind) => [
        kind,
        { scope: 'errors', status: 'all', kind: 'all', search: '', page: 1 },
      ]),
    ) as Record<MonitorTaskKind, Required<MonitorTaskQuery>>,
  );
  const query = computed(() => queries[tab.value]);
  const list = ref<MonitorTaskPage>();
  const listLoading = ref(false);
  const listError = ref('');
  const loadedListKey = ref('');
  const listPath = computed(() =>
    tab.value === 'agent'
      ? '/admin/monitor/agent-runs'
      : tab.value === 'creative'
        ? '/admin/monitor/creative-tasks'
        : `/admin/monitor/queues/${encodeURIComponent(queueId.value)}/jobs`,
  );
  const listKey = computed(
    () =>
      `${listPath.value}?${new URLSearchParams(
        Object.entries(query.value)
          .filter(([, value]) => value !== '')
          .map(([key, value]) => [key, String(value)]),
      )}`,
  );
  const listCurrent = computed(() => listKey.value === loadedListKey.value);
  const drawerOpen = ref(false);
  const detail = ref<MonitorServiceView | MonitorTaskView>();
  const detailLoading = ref(false);
  const detailError = ref('');
  const selected = ref<{ kind: 'service' | MonitorTaskKind; id: string; queueId?: string }>();
  let listRequest = 0;
  let overviewRequest = 0;
  let detailRequest = 0;
  let requestedListKey = '';

  const filteredServices = computed(() => {
    const order = { foundation: 0, realtime: 1, async: 2, external: 3 };
    return (overview.value?.services ?? [])
      .filter(
        (row) =>
          (serviceFilter.value.group === 'all' || row.group === serviceFilter.value.group) &&
          (serviceFilter.value.status === 'all' ||
            (serviceFilter.value.status === 'unknown'
              ? row.status === 'unknown'
              : ['degraded', 'unavailable'].includes(row.status))) &&
          row.name
            .toLocaleLowerCase()
            .includes(serviceFilter.value.search.trim().toLocaleLowerCase()),
      )
      .sort((a, b) => order[a.group] - order[b.group]);
  });
  const abnormalCount = computed(
    () =>
      overview.value?.services.filter((row) => ['degraded', 'unavailable'].includes(row.status))
        .length ?? 0,
  );
  const unknownCount = computed(
    () => overview.value?.services.filter((row) => row.status === 'unknown').length ?? 0,
  );

  async function loadOverview(): Promise<void> {
    if (overviewLoading.value) return;
    const request = ++overviewRequest;
    overviewLoading.value = true;
    overviewError.value = '';
    try {
      const result = await adminApi.get<SystemMonitorOverview>('/admin/monitor/overview');
      if (request === overviewRequest) overview.value = result;
    } catch {
      if (request === overviewRequest)
        overviewError.value = overview.value
          ? '服务刷新失败，保留上次采样。请重试。'
          : '服务诊断加载失败，请检查后台服务和监控权限后重试。';
    } finally {
      if (request === overviewRequest) overviewLoading.value = false;
    }
  }

  async function loadTasks(): Promise<void> {
    if (page.value !== 'monitor') return;
    const key = listKey.value;
    if (listLoading.value && requestedListKey === key) return;
    requestedListKey = key;
    const request = ++listRequest;
    listLoading.value = true;
    listError.value = '';
    try {
      const result = await adminApi.get<MonitorTaskPage>(key);
      if (request !== listRequest || key !== listKey.value || page.value !== 'monitor') return;
      list.value = result;
      loadedListKey.value = key;
      query.value.page = Math.min(
        query.value.page,
        Math.max(1, Math.ceil(result.total / result.limit)),
      );
    } catch {
      if (request === listRequest) listError.value = '任务诊断加载失败，请重试。';
    } finally {
      if (request === listRequest) listLoading.value = false;
    }
  }

  async function loadOnlineUsers(): Promise<void> {
    if (page.value !== 'monitor') return;
    const key = onlineKey.value;
    if (onlineLoading.value && requestedOnlineKey === key) return;
    requestedOnlineKey = key;
    const request = ++onlineRequest;
    onlineLoading.value = true;
    onlineError.value = '';
    try {
      const result = await adminApi.get<MonitorOnlineUserPage>(key);
      if (request !== onlineRequest || key !== onlineKey.value || page.value !== 'monitor') return;
      onlineUsers.value = result;
      loadedOnlineKey.value = key;
      onlineQuery.page = Math.min(
        onlineQuery.page,
        Math.max(1, Math.ceil(result.total / result.limit)),
      );
    } catch {
      if (request === onlineRequest)
        onlineError.value = '在线用户刷新失败，请重试；已有结果为上次采样。';
    } finally {
      if (request === onlineRequest) onlineLoading.value = false;
    }
  }

  async function loadDetail(): Promise<void> {
    const selection = selected.value;
    if (!selection || !drawerOpen.value) return;
    const request = ++detailRequest;
    detailLoading.value = true;
    detailError.value = '';
    const path =
      selection.kind === 'service'
        ? '/admin/monitor/services'
        : selection.kind === 'agent'
          ? '/admin/monitor/agent-runs'
          : selection.kind === 'creative'
            ? '/admin/monitor/creative-tasks'
            : `/admin/monitor/queues/${encodeURIComponent(selection.queueId ?? '')}/jobs`;
    try {
      const result = await adminApi.get<MonitorServiceView | MonitorTaskView>(
        `${path}/${encodeURIComponent(selection.id)}`,
      );
      if (request === detailRequest && drawerOpen.value) detail.value = result;
    } catch {
      if (request === detailRequest)
        detailError.value = '详情读取失败，记录可能已清理或暂不可用。请重试。';
    } finally {
      if (request === detailRequest) detailLoading.value = false;
    }
  }

  function openDetail(kind: 'service' | MonitorTaskKind, id: string): void {
    selected.value = { kind, id, queueId: kind === 'queue' ? queueId.value : undefined };
    detail.value = undefined;
    drawerOpen.value = true;
    void loadDetail();
  }
  function closeDetail(): void {
    drawerOpen.value = false;
    ++detailRequest;
    detailLoading.value = false;
  }
  function openQueue(id: string): void {
    queueId.value = id;
    openTaskStatus('queue', 'all', 'active');
  }
  function activatePage(name: 'monitor' | 'services'): void {
    closeDetail();
    if (page.value !== name) {
      ++listRequest;
      listLoading.value = false;
      ++onlineRequest;
      onlineLoading.value = false;
    }
    page.value = name;
  }
  function openTaskStatus(kind: MonitorTaskKind, status: string, scope: 'active' | 'errors'): void {
    const value = queries[kind];
    Object.assign(value, { scope, status, kind: 'all', search: '', page: 1 });
    tab.value = kind;
  }
  async function refresh(): Promise<void> {
    await Promise.all([
      loadOverview(),
      page.value === 'monitor' ? loadTasks() : Promise.resolve(),
      page.value === 'monitor' ? loadOnlineUsers() : Promise.resolve(),
      drawerOpen.value ? loadDetail() : Promise.resolve(),
    ]);
  }
  for (const value of Object.values(queries))
    watch(
      () => [value.scope, value.status, value.kind, value.search],
      () => {
        value.page = 1;
      },
      { flush: 'sync' },
    );
  watch(
    queueId,
    () => {
      queries.queue.page = 1;
    },
    { flush: 'sync' },
  );
  watch(
    () => [onlineQuery.search, onlineQuery.client, onlineQuery.state],
    () => {
      onlineQuery.page = 1;
    },
    { flush: 'sync' },
  );
  watch(onlineKey, () => {
    void loadOnlineUsers();
  });
  watch(
    () => (page.value === 'monitor' ? listKey.value : ''),
    () => {
      void loadTasks();
    },
  );

  return {
    overview,
    overviewLoading,
    overviewError,
    autoRefresh,
    onlineUsers,
    onlineLoading,
    onlineError,
    onlineQuery,
    onlineCurrent,
    loadOnlineUsers,
    serviceFilter,
    tab,
    queueId,
    query,
    queries,
    list,
    listLoading,
    listError,
    listCurrent,
    drawerOpen,
    detail,
    detailLoading,
    detailError,
    selected,
    filteredServices,
    abnormalCount,
    unknownCount,
    loadOverview,
    loadTasks,
    loadDetail,
    openDetail,
    closeDetail,
    openQueue,
    activatePage,
    openTaskStatus,
    refresh,
  };
});
